import React, { useCallback, useMemo, useState } from 'react';
import { AppDashboardNamedDataType, AppDashboardSplitType } from '../../../../types/generated';
import { bokunTheme } from '../../../../index';
import { OnChartHover } from '../OnChartHover';
import Typography from '@material-ui/core/Typography';
import { LineChartComponent } from './LineChartComponent';
import { format } from 'date-fns';

interface InstallsChartProps {
    split: AppDashboardSplitType;
    installations: AppDashboardNamedDataType[];
    uninstallations: AppDashboardNamedDataType[];
}

export const InstallsChart = ({
    split,
    installations,
    uninstallations,
}: InstallsChartProps): JSX.Element => {
    const initialInstallData = useMemo<OnChartHover[]>(
        () => [
            {
                value: installations.map(i => i.amount).reduce((a, b) => a + b, 0),
                color: bokunTheme.primary,
                dataKey: 'Installs',
            },
        ],
        [installations]
    );
    const [installData, setInstallData] = useState<OnChartHover[]>(initialInstallData);

    // Make sure it doesn't re-render and mess up the Tooltip line in the charts component
    const updateInstallData = useCallback(
        payload => {
            if (payload !== null) {
                if (JSON.stringify(payload) !== JSON.stringify(installData)) {
                    setInstallData(payload);
                }
            }
        },
        [installData]
    );

    const labelFormatter = (label: string) => {
        const date = new Date(label);

        switch (split) {
            case 'DAILY': {
                return format(date, 'd. MMM yyyy');
            }
            case 'MONTHLY': {
                return format(date, 'MMM yyyy');
            }
            case 'WEEKLY': {
                return 'Week(' + format(date, 'w) MMM yyyy');
            }
        }

        return date.toString();
    };

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                {installData.map((d, dIndex) => (
                    <div key={dIndex}>
                        <Typography gutterBottom variant="h5" component="h2">
                            {d.dataKey}
                        </Typography>
                        <Typography
                            gutterBottom
                            variant="h6"
                            component="h4"
                            style={{ color: d.color }}
                        >
                            {d.value}
                        </Typography>
                    </div>
                ))}
            </div>
            <LineChartComponent
                dataKey={'Installs'}
                secondaryDataKey={'Uninstalls'}
                data={installations}
                secondaryData={uninstallations}
                tooltipDataKey={'label'}
                tooltipLabelFormatter={labelFormatter}
                reset={() => updateInstallData(initialInstallData)}
                onHover={updateInstallData}
            />
        </>
    );
};
